import { BiSolidHelpCircle } from "solid-icons/bi";
import { BsCardText } from "solid-icons/bs";
import { ImDisplay } from "solid-icons/im";
import { IoClose, IoShield } from "solid-icons/io";
import { type JSXElement, Show, onCleanup, onMount } from "solid-js";

import { useGlobalContext } from "../../context/Global";
import type { DictKey } from "../../i18n/i18n";
import "../../style/settings.scss";
import { isMobile } from "../../utils/helper";
import Denomination from "./Denomination";
import Logs from "./Logs";
import PrivacyMode from "./PrivacyMode";
import RescueFile from "./RescueKey";
import Separator from "./Separator";
import Tooltip from "./Tooltip";
import ZeroConf from "./ZeroConf";

const Section = (props: {
    title: string;
    icon: JSXElement;
    children: JSXElement;
}) => {
    return (
        <div class="section-container">
            <h3 class="section-title">
                {props.icon}
                <span>{props.title}</span>
            </h3>
            {props.children}
        </div>
    );
};

const Entry = (props: {
    label: DictKey;
    tooltipLabel: DictKey;
    settingElement: JSXElement;
}) => {
    const { t } = useGlobalContext();

    return (
        <span class="setting">
            <label>
                {t(props.label)}:
                <Tooltip label={{ key: props.tooltipLabel }}>
                    <BiSolidHelpCircle size={18} opacity={0.5} />
                </Tooltip>
            </label>
            <div class="spacer" />
            {props.settingElement}
        </span>
    );
};

const SettingsMenuContent = () => {
    const { t, setSettingsMenu } = useGlobalContext();

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setSettingsMenu(false);
        }
    };
    const preventPageScroll = (event: WheelEvent | TouchEvent) => {
        event.preventDefault();
    };
    const stopPropagation = (event: MouseEvent | WheelEvent | TouchEvent) => {
        event.stopPropagation();
    };

    onMount(() => document.addEventListener("keydown", handleKeyDown));
    onCleanup(() => document.removeEventListener("keydown", handleKeyDown));

    return (
        <div
            id="settings-menu"
            onClick={() => setSettingsMenu(false)}
            on:touchmove={preventPageScroll}
            on:wheel={preventPageScroll}>
            <div
                class="frame assets-select settings-menu-panel"
                role="dialog"
                aria-modal="true"
                onClick={stopPropagation}
                on:touchmove={stopPropagation}
                on:wheel={stopPropagation}>
                <div class="settings-menu-header">
                    <h2>{t("settings")}</h2>
                    <span
                        class="close settings-menu-close"
                        onClick={() => setSettingsMenu(false)}>
                        <IoClose />
                    </span>
                </div>
                <div
                    class="settings-menu-scroll"
                    on:touchmove={stopPropagation}
                    on:wheel={stopPropagation}>
                    <hr class="spacer" />
                    <Section
                        title={t("display")}
                        icon={<ImDisplay size={20} />}>
                        <Entry
                            label={"denomination"}
                            tooltipLabel={"denomination_tooltip"}
                            settingElement={<Denomination />}
                        />
                        <Entry
                            label={"decimal_separator"}
                            tooltipLabel={"decimal_tooltip"}
                            settingElement={<Separator />}
                        />
                    </Section>

                    <Section
                        title={t("security")}
                        icon={<IoShield size={20} />}>
                        <Entry
                            label={"hide_wallet_address"}
                            tooltipLabel={"hide_wallet_address_tooltip"}
                            settingElement={<PrivacyMode />}
                        />
                        <Show when={!isMobile()}>
                            <Entry
                                label={"zero_conf"}
                                tooltipLabel={"zero_conf_tooltip"}
                                settingElement={<ZeroConf />}
                            />
                        </Show>
                        <Entry
                            label={"rescue_key"}
                            tooltipLabel={"download_boltz_rescue_key"}
                            settingElement={<RescueFile />}
                        />
                    </Section>

                    <Section
                        title={t("support")}
                        icon={<BsCardText size={20} />}>
                        <Entry
                            label={"logs"}
                            tooltipLabel={"logs_tooltip"}
                            settingElement={<Logs />}
                        />
                    </Section>
                </div>
            </div>
        </div>
    );
};

const SettingsMenu = () => {
    const { settingsMenu } = useGlobalContext();

    return (
        <Show when={settingsMenu()}>
            <SettingsMenuContent />
        </Show>
    );
};

export default SettingsMenu;
